// Plain-English formatting shared by both dashboards (and the mock, so the
// mock's `display` strings read the same as the engine's).
import type { AssetCategory, ClaimStatus, DocumentClass, Provider, Source, ValueBasis } from './api/types.ts';

const gbp0 = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const gbp2 = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 });

/** £1,499 for whole pounds, £56.19 otherwise. */
export function money(pence: number | null | undefined): string {
  if (pence == null) return '–';
  return pence % 100 === 0 ? gbp0.format(pence / 100) : gbp2.format(pence / 100);
}

const dateFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' });
const dateTimeFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/London',
});
const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });

export function date(iso: string | null | undefined): string {
  if (!iso) return '–';
  return dateFmt.format(new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso));
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  return dateTimeFmt.format(new Date(iso));
}

export function time(iso: string): string {
  return timeFmt.format(new Date(iso));
}

/** Whole days from now until a YYYY-MM-DD date (negative when past). */
export function daysUntil(day: string, now = new Date()): number {
  const target = Date.parse(`${day}T00:00:00Z`);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86_400_000);
}

/** "3 min ago", "5 h ago", "2 days ago". */
export function ago(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  const d = Math.round(s / 86_400);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

/** "4 h 10 min", for SLA clocks. */
export function duration(ms: number): string {
  const m = Math.round(Math.abs(ms) / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} days`;
}

export function percent(n: number | null | undefined): string {
  return n == null ? '–' : `${Math.round(n * 100)}%`;
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Where a fact came from, in the customer's terms. */
export const SOURCE_LABEL: Record<Source, string> = {
  chat: 'You told us in chat',
  voice: 'From your voice note',
  photo: 'From a photo',
  document: 'From a document',
  companies_house: 'From Companies House',
  freeagent: 'From FreeAgent',
  xero: 'From Xero',
  email: 'From a forwarded email',
  bank_csv: 'From a bank statement',
  dashboard: 'You added it here',
  staff: 'Added by our team',
};

/** Short source names for tables (ops). */
export const SOURCE_SHORT: Record<Source, string> = {
  chat: 'Chat',
  voice: 'Voice note',
  photo: 'Photo',
  document: 'Document',
  companies_house: 'Companies House',
  freeagent: 'FreeAgent',
  xero: 'Xero',
  email: 'Email forward',
  bank_csv: 'Bank CSV',
  dashboard: 'Dashboard',
  staff: 'Staff',
};

export const CATEGORY_LABEL: Record<AssetCategory, string> = {
  power_tools: 'Power tools',
  hand_tools: 'Hand tools',
  test_equipment: 'Test equipment',
  plant: 'Plant',
  ladders_access: 'Ladders and access',
  vehicle: 'Vehicles',
  electronics: 'Electronics',
  materials: 'Materials',
  other: 'Other',
};

export const VALUE_BASIS_LABEL: Record<ValueBasis, string> = {
  receipt: 'Receipt',
  accounts: 'From accounts',
  bank_payment: 'Bank payment',
  customer: 'Your figure',
  estimate: 'Estimate',
};

export const DOC_CLASS_LABEL: Record<DocumentClass, string> = {
  quote: 'Quote',
  claim_acknowledgement: 'Claim acknowledgement',
  email: 'Email',
  receipt: 'Receipt',
  invoice: 'Invoice',
  tool_photo: 'Tool photo',
  damage_photo: 'Damage photo',
  police_report: 'Police report',
  contract: 'Contract',
  policy_schedule: 'Policy schedule',
  certificate: 'Certificate',
  id: 'ID',
  bank_statement: 'Bank statement',
  other: 'Other',
};

export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  in_review: 'In review',
  awaiting_info: 'Waiting for info',
  closed: 'Closed',
};

export const PROVIDER_LABEL: Record<Provider, string> = {
  freeagent: 'FreeAgent',
  xero: 'Xero',
  email_forward: 'Email forwarding',
  bank_csv: 'Bank statement upload',
  companies_house: 'Companies House',
};

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

const CHANNEL_LABEL: Record<string, string> = { whatsapp: 'WhatsApp', web: 'Website chat', email: 'Email', voice: 'Voice', sms: 'SMS', simulator: 'Simulator' };

/** "WhatsApp", "Website chat", … (unknown channels show as sent). */
export function channelLabel(channel: string | null | undefined): string {
  return channel ? (CHANNEL_LABEL[channel] ?? channel) : '–';
}

/**
 * A contact's phone for display, or null when there isn't a real one: a
 * website visitor's `phone` is `web:<id>` (and `phoneMasked` "Website visitor")
 * until they move to WhatsApp.
 */
export function displayPhone(phone: string | null | undefined): string | null {
  return phone && /^[+\d]/.test(phone) ? phone : null;
}

/** "Charlie" from "Charlie Bailey" (or "Charlie Bailey (duplicate …)"): for greetings. */
export function firstName(displayName: string | null | undefined): string | null {
  const first = displayName?.replace(/\(.*?\)/g, '').trim().split(/\s+/)[0];
  return first && /\p{L}/u.test(first) ? first : null;
}

/**
 * What to call a contact: their name, else their phone, else their email,
 * else what kind of contact they are ("Website visitor", "Email", "Online
 * account": the engine's phoneMasked for contacts without a phone).
 */
export function contactLabel(c: { displayName?: string | null; contactName?: string | null; email?: string | null; phoneMasked?: string | null } | null | undefined): string {
  return c?.displayName ?? c?.contactName ?? displayPhone(c?.phoneMasked) ?? c?.email ?? (c?.phoneMasked || 'Website visitor');
}
